import { Component, OnInit, TemplateRef, ViewChild, inject, HostListener } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { ActivatedRoute, Router } from '@angular/router';
import {
  Alert,
  AlertService,
  AlertType,
  ModalComponent,
  ModalResult,
  PaginatorFetcher,
  PaginatorFilters,
  TableConfig
} from '@michelin/theme';
import { Store } from '@ngxs/store';
import { map } from 'rxjs';
import { getCountryStateList } from 'src/app/shared/country/CountryState';
import { CanComponentDeactivate } from 'src/app/shared/guards/unsaved-changes.guard';
import { UtilityService } from 'src/app/shared/services/utility.service';
import { UpdateCustomerInfoAction } from 'src/app/shared/state/CustomerInfoState';
import { DealerState } from 'src/app/shared/state/DealerState';
import { FetchAllCustomerReferentialsAction } from 'src/app/shared/state/ProductsState';
import { TranslationState } from 'src/app/shared/state/TranslationState';
import { TranslationService } from 'src/app/shared/translations/services/translation.service';
import { PdfExportService } from '../../../../shared/services/pdf-export.service';
import { Specification } from '../../../asset-management/models/Specification';
import { AssetManagementService } from '../../../asset-management/services/asset-management.service';
import { ROLES } from '../../../authentication/authRoles.constants';
import { Referential, getCustomerStockAllReferential } from '../../models/CustomerReferential';
import {
  AssociateDealerName,
  CamSpecName,
  CustomerInfoCam,
  CustomerSetupResponse,
  PaginatedCustomerInfoResponse,
  Pagination
} from '../../models/response.model';
import {
  getAllCustomerDropdownKeys,
  getCustomerFormGroup,
  getDropdownOptions,
  mapToCustomerDetailsRequest,
  populateDropdowns,
  populateToggleButtons
} from '../../services/CustomerUtils';
import { CustomerService } from '../../services/customer.service';
import { environment } from '../../../../../environments/environment';

declare const gtag: (
  type: string,
  name: string,
  options: {
    event_category: string;
    event_label: string;
    value: string;
  }
) => void;

@Component({
  selector: 'app-customer-setup',
  templateUrl: './customer-setup.component.html',
  styleUrls: ['./customer-setup.component.scss', '../../customer-shared.scss'],
  standalone: false
})
export class CustomerSetupComponent implements OnInit, CanComponentDeactivate {
  @ViewChild('viewCamModal', { static: true }) viewCamModal: ModalComponent;
  @ViewChild('confirmation') viewCamContentModal: TemplateRef<any>;
  @ViewChild('unsavedChangesModal', { static: true }) unsavedChangesModal: ModalComponent;
  @ViewChild('unsavedConfirmation') unsavedChangesContentModal: TemplateRef<any>;

  customerFormGroup: FormGroup;
  initialFormValues: any;
  customer: CustomerSetupResponse;
  customerId: string;
  specification: Specification | undefined;

  camSpecNames = [];
  associateDealerNames = [];
  associateDealers: AssociateDealerName[] = [];
  camSpecsOptions: CamSpecName[] = [];
  countryStates = getCountryStateList();
  dropdownOptions: { [key: string]: Referential[] } = {};
  finishedLabelOptions: Referential[];
  rejectReturnLabelOptions: Referential[];
  rejectScrapLabelOptions: Referential[];
  barcodeOptions: Referential[];

  readOnlyMode = false;
  viewMode = 'edit-component';
  loading = true;
  isSaving = false;
  referentialLoading = true;

  currentPage = 1;
  pageSize = 10;
  pageSizeOptions = [1, 5, 10, 50, 100];
  associateCustomers: CustomerInfoCam[] = [];
  customerPagination: Pagination;
  isActiveToggleChecked = false;
  showAssociateToField = false;
  createMode = false;
  inheritedAssociateDealerId = null;

  tableConfig: TableConfig<CustomerInfoCam> = {
    paginator: {
      pageSize: this.pageSize,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom'
    },
    onColumnsChange: (columns: string[]) => console.log('Column display change', columns),
    columns: {}
  };

  public translationService = inject(TranslationService);
  public assetManagementService = inject(AssetManagementService);
  public customerService = inject(CustomerService);
  public alertService = inject(AlertService);
  public store = inject(Store);
  public router = inject(Router);
  public activatedRoute = inject(ActivatedRoute);
  public formBuilder = inject(FormBuilder);
  public pdfExportService = inject(PdfExportService);
  public utilityService = inject(UtilityService);

  constructor() {
    this.activatedRoute.params.subscribe((params) => {
      this.customerId = params['customerId'];
    });
  }

  ngOnInit(): void {
    this.createMode = this.customerId === 'create';
    this.customerFormGroup = getCustomerFormGroup(this.formBuilder);

    this.fetchCustomerDetails();
    this.fetchCamSpecOptions();

    this.viewAssociateCustomerList();
    this.customerFormGroup
      .get('customerInfo')
      .get('customerType')
      .valueChanges.subscribe((value) => {
        this.showAssociateToField = value === 'AssociateCustomer';
      });
  }

  private fetchCamSpecOptions() {
    this.customerService.getAllCamSpecNames().subscribe({
      next: (response) => {
        this.camSpecNames = response.data.map((camSpecNames) => camSpecNames.name);
        this.camSpecsOptions = response.data;
      },
      error: (err) => {
        console.log(err);
        this.alertService.showAlert({
          type: AlertType.strong,
          content: 'Please try again. Something went wrong.'
        } as Alert);
      }
    });
  }

  private fetchCustomerDetails() {
    this.customerService.getCustomerSettings(this.customerId).subscribe({
      next: (response) => {
        this.loadAllDropdowns(response.data);
        this.loading = false;
      },
      error: (err) => {
        console.log(err);
        this.alertService.showAlert({
          type: AlertType.strong,
          content: 'Please try again. Something went wrong.'
        } as Alert);
      }
    });
  }

  initializeCustomerDetails(res: CustomerSetupResponse) {
    this.customer = res;
    this.customerFormGroup.patchValue(this.customer);
    this.updateToggleValues();

    populateDropdowns(this.customerFormGroup, this.dropdownOptions, this.customer);
    populateToggleButtons(this.customerFormGroup, this.customer);

    this.readOnlyMode =
      !this.createMode &&
      this.customer?.customerSettings.customerImportEnabled &&
      !['AssociateCustomer', 'DefaultCustomer'].includes(this.customer?.customerInfo.customerType);
    this.viewMode = this.readOnlyMode ? 'view-component' : 'edit-component';
    if (this.readOnlyMode) {
      this.customerFormGroup.get('customerInfo').get('countryCode').disable();
    }
    this.showAssociateToField = this.customer?.customerInfo.customerType === 'AssociateCustomer';
    this.associateDealerNames = res.associateDealers.map((names) => names.customerName);
    this.associateDealers = res.associateDealers;
    if (this.createMode) {
      if (this.customer?.customerSettings.customerImportEnabled) {
        this.customerInfoFormGroup.get('customerType').patchValue('AssociateCustomer');
        this.customerInfoFormGroup.get('customerType').disable();
      } else {
        this.customerInfoFormGroup.get('customerType').patchValue('Customer');
      }
    }
    if (['AssociateCustomer', 'DefaultCustomer'].includes(this.customer?.customerInfo.customerType)) {
      this.customerInfoFormGroup.get('customerType').disable();
    }
    this.initialFormValues = this.customerFormGroup.value;
    // Update customer info in store for customer cam export pdf generation
    this.store.dispatch(new UpdateCustomerInfoAction(this.customer));
  }

  saveChanges(): void {
    if (!this.isFormChanged()) {
      this.utilityService.showAlert(AlertType.strong, this.translationService.translate('noModifiedText'), '');
      return;
    }

    this.customerFormGroup.markAllAsTouched();

    if (this.customerFormGroup.valid) {
      this.isSaving = true;
      if (this.customerInfoFormGroup.get('customerType').value === 'AssociateCustomer') {
        this.customer.customerInfo.associatedTo = this.associateDealers
          .find((dealer) => dealer.customerName === this.customerFormGroup.get('customerInfo').get('associatedTo').value)
          .id.toString()
          .toUpperCase();
      }
      if (environment.envName !== 'dev') {
        this.applyGoogleAnalyticsEvent();
      }
      const customerDetailsRequest = mapToCustomerDetailsRequest(
        this.customer,
        this.customerFormGroup.getRawValue(),
        this.camSpecsOptions,
        this.dropdownOptions['PrintingTemplate']
      );
      const user = this.store.selectSnapshot(DealerState.currentUser);

      this.customerService.updateCustomerSettings(customerDetailsRequest, this.customer.customerInfo.customerPk, user).subscribe({
        next: (res) => {
          this.createMode = false;
          this.initializeCustomerDetails(res.data);
          this.isSaving = false;
          this.utilityService.showAlert(AlertType.success, this.translationService.translate('camSaveSuccessText'), '');
        },
        error: () => {
          this.isSaving = false;
          this.utilityService.showAlert(
            AlertType.critical,
            this.translationService.translate('criticalErrorText'),
            this.translationService.translate('criticalErrorAlertText')
          );
        }
      });
    } else {
      const missingFields: string[] = this.getMissingMandatoryFields();

      if (missingFields.length > 0) {
        const fieldList = missingFields.join(', ');
        this.utilityService.showAlert(
          AlertType.strong,
          this.translationService.translate('incorrectValuesText'),
          `${this.translationService.translate('mandatoryMissingText')} ${fieldList}`
        );
      } else {
        this.utilityService.showAlert(
          AlertType.strong,
          this.translationService.translate('incorrectValuesText'),
          this.translationService.translate('incorrectValuesOnCustomerSaveAlertText')
        );
      }
    }
  }

  getMissingMandatoryFields(): string[] {
    const missingFields: string[] = [];

    const customerInfoGroup = this.customerFormGroup.get('customerInfo');
    const customerCamSpecGroup = this.customerFormGroup.get('customerCamSpec');
    const customerSettingsGroup = this.customerFormGroup.get('customerSettings');

    if (customerInfoGroup) {
      if (!customerInfoGroup.get('code')?.value) {
        missingFields.push('Customer Code');
      }
      if (!customerInfoGroup.get('customerName')?.value) {
        missingFields.push('Customer Name');
      }
      if (!customerInfoGroup.get('customerType')?.value) {
        missingFields.push('Customer Type');
      }
      if (!customerCamSpecGroup.get('camSpecName')?.value) {
        missingFields.push('CAM Spec Name');
      }
      if (!customerSettingsGroup.get('turnAroundTime')?.value) {
        missingFields.push('Turnaround Time');
      }
    }
    return missingFields;
  }

  showViewCamSPecModal(): void {
    this.customerFormGroup.markAsUntouched();
    if (this.customer.customerCamSpec.camSpecId.length > 0) {
      this.viewCamModal.open({
        id: 'confirmation',
        width: 500,
        title: this.translationService.translate('redirectCamText'),
        content: this.viewCamContentModal,
        confirm: this.translationService.translate('okText'),
        cancel: this.translationService.translate('cancelLinkText')
      });
    }
  }

  exportCamSpecWithCustomer() {
    const camId = this.camSpecsOptions
      .find((camSpec) => camSpec.name === this.customerFormGroup.get('customerCamSpec').get('camSpecName').value)
      .id.toString()
      .toLowerCase();
    this.assetManagementService.getSpecification(camId).subscribe((res) => {
      this.specification = res.data;
      this.pdfExportService.generatePDF(this.specification.name, this.specification.specificationLines, true);
    });
  }

  onViewCamModalClosed(result: ModalResult) {
    if (result && result.result) {
      const camId = this.camSpecsOptions
        .find((camSpec) => camSpec.name === this.customerFormGroup.get('customerCamSpec').get('camSpecName').value)
        .id.toString()
        .toLowerCase();
      this.router.navigateByUrl(`home/specifications/${camId}`);
    }
  }

  canEditCustomerSettings(): boolean {
    const roles: string = localStorage.getItem('roles');
    return roles.includes(ROLES.edit_customer);
  }

  isSaveButtonDisabled(): boolean {
    return !this.canEditCustomerSettings() || !this.isFormChanged();
  }

  getSaveButtonTooltip(): string {
    if (!this.canEditCustomerSettings()) {
      return this.translationService.translate('userCannotSaveText');
    }
    if (!this.isFormChanged()) {
      return this.translationService.translate('noValuesHaveBeenModified');
    }
    return '';
  }

  viewAssociateCustomerList() {
    this.store.select(TranslationState.currentLanguage).subscribe(() => {
      this.tableConfig.columns = {
        customerCode: {
          cell: (elm: CustomerInfoCam) => elm.customerCode.toString(),
          header: this.translationService.translate('customerCodeText')
        },
        customerName: {
          cell: (elm: CustomerInfoCam) => elm.customerName,
          header: this.translationService.translate('customerNameText')
        },
        city: {
          cell: (elm: CustomerInfoCam) => elm.city.toString(),
          header: this.translationService.translate('cityText')
        },
        state: { cell: (elm: CustomerInfoCam) => elm.state, header: this.translationService.translate('customerStateProvinceText') },
        shipTo: { cell: (elm: CustomerInfoCam) => elm.shipTo, header: this.translationService.translate('shipToText') },
        status: { cell: 'activeCell', header: this.translationService.translate('statusColumnText') }
      };
    });
  }

  associateCustomerTableFetcher: PaginatorFetcher<CustomerInfoCam> = (filters: PaginatorFilters) => {
    this.currentPage = filters.page;
    this.pageSize = filters.limit;
    return this.customerService.getAssociateCustomers(this.customer.customerInfo.customerPk, this.currentPage, this.pageSize).pipe(
      map((customerInfoResponse: PaginatedCustomerInfoResponse) => {
        this.associateCustomers = customerInfoResponse.data.customerInfos;
        this.customerPagination = customerInfoResponse.data.pagination;
        return {
          items: this.associateCustomers,
          count: this.customerPagination.totalRecords
        };
      })
    );
  };

  loadAllDropdowns(customer: CustomerSetupResponse) {
    this.store.dispatch(new FetchAllCustomerReferentialsAction()).subscribe(() => {
      const dropdownKeys = getAllCustomerDropdownKeys();
      dropdownKeys.forEach((key) => {
        this.dropdownOptions[key] = getDropdownOptions(key, this.store);
      });
      this.dropdownOptions['RememberBrand'] = getCustomerStockAllReferential();
      this.dropdownOptions['RememberCasingNote'] = getCustomerStockAllReferential();

      this.initializeCustomerDetails(customer);
      this.populateLabelDropdowns();
      this.referentialLoading = false;
    });
  }

  get customerSettingsFormGroup() {
    return this.customerFormGroup.get('customerSettings') as FormGroup;
  }

  get customerInfoFormGroup() {
    return this.customerFormGroup.get('customerInfo') as FormGroup;
  }

  get customerCamSpecFormGroup() {
    return this.customerFormGroup.get('customerCamSpec') as FormGroup;
  }

  inactiveToActive(event: MatSlideToggleChange) {
    if (event.checked) {
      this.isActiveToggleChecked = true;
      if (this.customer.customerCamSpec.camSpecId === null) {
        //Changing the cam spec to Default
        this.customerFormGroup.get('customerCamSpec').patchValue({
          camSpecName: this.customer.defaultCamSpec.camSpecName,
          repairLimitsCheckApplyWhen: this.customer.defaultCamSpec.repairLimitsCheckApplyWhen
        });
        this.customer.customerCamSpec.camSpecId = this.customer.defaultCamSpec.camSpecId;
      }
    } else {
      this.isActiveToggleChecked = false;
    }
  }

  populateLabelDropdowns() {
    this.finishedLabelOptions = this.dropdownOptions['PrintingTemplate'].filter((option) => option.description === 'FINISHED_GOODS');
    this.rejectReturnLabelOptions = this.dropdownOptions['PrintingTemplate'].filter((option) => option.description === 'REJECT_RETURN');
    this.rejectScrapLabelOptions = this.dropdownOptions['PrintingTemplate'].filter((option) => option.description === 'REJECT_SCRAP');
    this.barcodeOptions = this.dropdownOptions['PrintingTemplate'].filter((option) => option.description === 'BAR_CODE');
  }

  inheritSetting() {
    this.inheritedAssociateDealerId = this.associateDealers
      .find((dealer) => dealer.customerName === this.customerFormGroup.get('customerInfo').get('associatedTo').value)
      .id.toString()
      .toUpperCase();
    this.customerService.getCustomerSettings(this.inheritedAssociateDealerId).subscribe({
      next: (response) => {
        this.customerFormGroup.get('customerSettings').patchValue(response.data.customerSettings);
        this.updateToggleValues();
        populateDropdowns(this.customerFormGroup, this.dropdownOptions, response.data);
        this.alertService.showAlert({
          type: AlertType.success,
          title: this.translationService.translate('inheritSettingAlertText').split('%')[0],
          content: this.translationService.translate('inheritSettingAlertText').split('%')[1]
        } as Alert);
      },
      error: (err) => {
        console.log(err);
        this.alertService.showAlert({
          type: AlertType.strong,
          content: 'Please try again. Something went wrong.'
        } as Alert);
      }
    });
  }

  updateToggleValues() {
    this.customerSettingsFormGroup.patchValue({
      wheelProcessing: this.customerSettingsFormGroup.get('wheelProcessing').value === 'Y',
      scrapCasingPlant: this.customerSettingsFormGroup.get('scrapCasingPlant').value === 'Y',
      centralReject: this.customerSettingsFormGroup.get('centralReject').value === 'Y',
      includeScrapCasingRejectCustomer: this.customerSettingsFormGroup.get('includeScrapCasingRejectCustomer').value === 'Y'
    });
  }

  canDeactivate(): boolean | Promise<boolean> {
    if (this.isFormChanged()) {
      this.unsavedChangesModal.open({
        id: 'unsaved-confirmation',
        width: 500,
        title: this.translationService.translate('discardChangesText'),
        content: this.unsavedChangesContentModal,
        confirm: this.translationService.translate('okText'),
        cancel: this.translationService.translate('cancelLinkText')
      });
      return new Promise<boolean>((resolve) => {
        this.unsavedChangesModal.result.subscribe((result: ModalResult) => {
          resolve(result?.result as boolean);
        });
      });
    }
    return true;
  }

  private isFormChanged(): boolean {
    const replacer = (key: string, value: any) => (typeof value === 'bigint' ? value.toString() : value);
    return JSON.stringify(this.initialFormValues, replacer) !== JSON.stringify(this.customerFormGroup.value, replacer);
  }

  checkIfCustomerCodeExists() {
    const customerCode = this.customerInfoFormGroup.get('code').value;
    if (customerCode && customerCode !== this.customer.customerInfo.code) {
      this.customerService.customerCodeExists(customerCode).subscribe((exists) => {
        if (exists) {
          this.customerInfoFormGroup.get('code').setErrors({ exists: true });
        } else {
          this.customerInfoFormGroup.get('code').setErrors(null);
        }
      });
    }
  }

  applyGoogleAnalyticsEvent() {
    if (this.customer.customerInfo.shipToCode !== this.customerInfoFormGroup.get('shipToCode').value) {
      gtag('event', 'customer_ship_to_changed', {
        event_category: 'VALUE_CHANGE',
        event_label: 'Customer Ship to Value Changed',
        value: `For dealer: ${window.localStorage.getItem('dealerId')}, customer: ${this.customer.customerInfo.code}`
      });
    }

    if (this.customer.customerSettings.michelinShipTo !== this.customerSettingsFormGroup.get('michelinShipTo').value) {
      gtag('event', 'customer_michelin_ship_to_changed', {
        event_category: 'VALUE_CHANGE',
        event_label: 'Customer Michelin Ship to Value Changed',
        value: `For dealer: ${window.localStorage.getItem('dealerId')}, customer: ${this.customer.customerInfo.code}`
      });
    }

    if (this.customer.customerCamSpec.camSpecName !== this.customerCamSpecFormGroup.get('camSpecName').value) {
      gtag('event', 'customer_cam_changed', {
        event_category: 'VALUE_CHANGE',
        event_label: 'Customer CAM Spec Changed',
        value: `For dealer: ${window.localStorage.getItem('dealerId')}, customer: ${this.customer.customerInfo.code}`
      });
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any): void {
    if (this.isFormChanged()) {
      $event.returnValue = true;
    }
  }
}
